// English translations
export const en = {
  // ============================================================================
  // Common / Shared
  // ============================================================================
  common: {
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    refresh: 'Refresh',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    confirm: 'Confirm',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    apply: 'Apply',
    add: 'Add',
    remove: 'Remove',
    view: 'View',
    viewAll: 'View All',
    viewDetails: 'View Details',
    showing: 'Showing',
    of: 'of',
    items: 'items',
    noData: 'No data available',
    noResults: 'No results found',
    retry: 'Retry',
    status: 'Status',
    actions: 'Actions',
    name: 'Name',
    description: 'Description',
    date: 'Date',
    time: 'Time',
    today: 'Today',
    yesterday: 'Yesterday',
    ago: 'ago',
    never: 'Never',
    enabled: 'Enabled',
    disabled: 'Disabled',
    active: 'Active',
    inactive: 'Inactive',
    all: 'All',
    none: 'None',
    select: 'Select',
    selectAll: 'Select All',
    required: 'Required',
    optional: 'Optional',
    default: 'Default',
  },

  // ============================================================================
  // General UI
  // ============================================================================
  general: {
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'This feature will be available in a future update.',
    more: 'More',
    noDataAvailable: 'No data available',
    tryAgain: 'Try Again',
    somethingWentWrong: 'Something went wrong',
    unexpectedError: 'An unexpected error occurred on this page.',
    resourceGone: 'Resource Gone',
    resourceGoneDescription: 'This resource has been permanently removed.',
    searchProducts: 'Search Products',
    dashboard: 'Dashboard',
  },

  // ============================================================================
  // Navigation / Sidebar
  // ============================================================================
  nav: {
    dashboard: 'Dashboard',
    workflow: 'Workflow',
    analysis: 'Analysis',
    discovery: 'Product Discovery',
    contentDiscovery: 'Content Discovery',
    sellers: 'Seller Overview',
    pricing: 'My Pricing',
    matching: 'Matching',
    aiAdvisor: 'AI Advisor',
    settings: 'Settings',
    roadmap: 'Roadmap',
    logout: 'Logout',
    profile: 'Profile',
    schedules: 'Schedules',
    billing: 'Billing',
    groupOverview: 'Overview',
    groupData: 'Data',
    groupExecution: 'Execution',
    groupTools: 'Tools & Settings',
  },

  // ============================================================================
  // Authentication
  // ============================================================================
  auth: {
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    welcomeBack: 'Welcome back',
    createAccount: 'Create Account',
    forgotPassword: 'Forgot password?',
    resetPassword: 'Reset Password',
    rememberMe: 'Remember me',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    apiKey: 'API Key',
    enterCredentials: 'Enter your username and password to sign in',
    enterApiKey: 'Enter your API key to sign in',
    invalidCredentials: 'Invalid credentials',
    loginFailed: 'Login failed. Please check your credentials.',
    loginSuccess: 'Successfully logged in',
    logoutSuccess: 'Successfully logged out',
    sessionExpired: 'Your session has expired. Please log in again.',
    // 2FA
    twoFactor: 'Two-Factor Authentication',
    enterCode: 'Please enter the code from your Authenticator App',
    invalidCode: 'Invalid 2FA code',
    // Register
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    agreeToTerms: 'I agree to the Terms of Service and Privacy Policy',
    registrationSuccess: 'Account created successfully',
    passwordRequirements: 'Password must be at least 8 characters',
  },

  // ============================================================================
  // Login Page
  // ============================================================================
  login: {
    welcomeBack: 'Welcome back',
    twoFactorAuth: 'Two-Factor Authentication',
    signInDescription: 'Enter your username and password to sign in',
    apiKeyDescription: 'Enter your API key to sign in',
    authenticatorDescription: 'Please enter the code from your Authenticator App',
    passwordButton: 'Password',
    apiKeyButton: 'API Key',
    usernameLabel: 'Username or Email',
    usernamePlaceholder: 'username or email@example.com',
    passwordLabel: 'Password',
    forgotPassword: 'Forgot password?',
    apiKeyLabel: 'API Key',
    apiKeyPlaceholder: 'c-FZnIdq...',
    apiKeyHelp: 'Your API key can be found in your profile settings',
    signingIn: 'Signing in...',
    signIn: 'Sign In',
    authCodeLabel: 'Authentication Code',
    authCodePlaceholder: '000 000',
    verifying: 'Verifying...',
    verifyCode: 'Verify Code',
    backToLogin: 'Back to Login',
    resetPassword: 'Reset Password',
    resetPasswordDescription: "Enter your email address and we'll send you a new password.",
    emailLabel: 'Email Address',
    emailPlaceholder: 'your@email.com',
    cancel: 'Cancel',
    sending: 'Sending...',
    sendResetEmail: 'Send Reset Email',
    passwordResetSent: 'Password reset email sent! Please check your inbox.',
  },

  // ============================================================================
  // Dashboard
  // ============================================================================
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Real-time market overview and performance metrics.',
    loading: 'Loading dashboard...',
    errorLoading: 'Failed to load dashboard data',
    allJobs: 'All Jobs',
    // KPIs
    totalProjects: 'Projects',
    totalTasks: 'Tasks',
    totalJobs: 'Jobs',
    productsExtracted: 'Extracted',
    productsNormalized: 'Normalized',
    productsMatched: 'Matched',
    activeProducts: 'Active Products',
    avgMarketPrice: 'Avg. Market Price',
    competitorsMonitored: 'Competitors',
    totalOffers: 'Total Offers',
    totalRevenue: 'Total Revenue',
    platformCoverage: 'Platform Coverage',
    // Charts
    domainCoverage: 'Domain Coverage',
    domainCoverageTooltip: 'Shows how many products are monitored per reference domain. Use the dropdown to switch between product count and offer count.',
    jobStatusDistribution: 'Job Status Distribution',
    jobStatusTooltip: 'Visual breakdown of all jobs by their current status. The percentage shows the completion rate.',
    jobStatusDescription: 'Job completion rate overview',
    rrpDeviation: 'RRP Deviation',
    // Job Status
    recentJobs: 'Recent Jobs',
    recentJobsDescription: 'Latest crawl job activity',
    recentJobsTooltip: 'Shows the 5 most recent jobs across all projects. Click "View All" to see the full workflow with all projects, tasks, and jobs.',
    jobStatus: 'Job Status',
    jobStatusSummaryTooltip: 'Summary of all jobs grouped by their status: Pending (waiting to run), Running (currently executing), Completed (finished successfully), Failed (encountered errors).',
    noJobs: 'No jobs yet',
    noDomains: 'No domains configured',
    // Status
    pending: 'Pending',
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
    // Competitors
    topCompetitors: 'Top Competitors',
    topDomains: 'Top Domains',
    topDomainsDescription: 'By reference URL coverage',
    topDomainsTooltip: 'Displays domains with the highest product overlap. Assortment shows matched/total products, Price Dev shows average price deviation from your prices.',
    competitor: 'Competitor',
    domain: 'Domain',
    assortment: 'Assortment',
    priceDev: 'Price Dev.',
    viewMatrix: 'View Matrix',
    // System
    systemActive: 'System Active',
    systemStable: 'System Stable',
    lastCrawl: 'Last crawl',
  },

  // ============================================================================
  // Workflow
  // ============================================================================
  workflow: {
    title: 'Workflow',
    subtitle: 'Manage your scraping projects, tasks, and jobs.',
    // Projects
    projects: 'Projects',
    project: 'Project',
    newProject: 'New Project',
    editProject: 'Edit Project',
    deleteProject: 'Delete Project',
    projectName: 'Project Name',
    projectDescription: 'Project Description',
    noProjects: 'No projects yet',
    createFirstProject: 'Create your first project to get started.',
    // Tasks
    tasks: 'Tasks',
    task: 'Task',
    newTask: 'New Task',
    editTask: 'Edit Task',
    deleteTask: 'Delete Task',
    taskName: 'Task Name',
    taskDescription: 'Task Description',
    noTasks: 'No tasks',
    selectProject: 'Select a project to view tasks.',
    // Jobs
    jobs: 'Jobs',
    job: 'Job',
    newJob: 'New Job',
    editJob: 'Edit Job',
    deleteJob: 'Delete Job',
    executeJob: 'Execute Job',
    stopJob: 'Stop Job',
    jobType: 'Job Type',
    jobConfig: 'Job Configuration',
    noJobs: 'No jobs',
    selectTask: 'Select a task to view jobs.',
    // URLs
    urls: 'URLs',
    masterUrl: 'Master URL',
    masterUrlLocked: 'Master URL is locked after jobs are created. Create a new task for a different master URL.',
    structuralChangeWarning: 'Changing platforms or URLs on a task with existing data may leave old results orphaned.',
    referenceUrls: 'Reference URLs',
    addUrl: 'Add URL',
    // CSV Mode
    csvMode: {
      urlCount: 'URLs',
      discoveryLabel: 'Auto-discover',
      crawlLimitCsv: 'Max URLs to process from uploaded list ({{total}} total)',
      crawlLimitHint: 'Crawl limit applies to uploaded URLs. {{total}} URLs uploaded.',
    },
    // Status
    jobPending: 'Pending',
    jobRunning: 'Running',
    jobCompleted: 'Completed',
    jobFailed: 'Failed',
    // Actions
    runPipeline: 'Run Pipeline',
    viewResults: 'View Results',
    viewSelectors: 'View Selectors',
    // Confirmations
    confirmDeleteProject: 'Are you sure you want to delete this project? All tasks and jobs will be deleted.',
    confirmDeleteTask: 'Are you sure you want to delete this task? All jobs will be deleted.',
    confirmDeleteJob: 'Are you sure you want to delete this job?',
  },

  // ============================================================================
  // Price Analysis
  // ============================================================================
  analysis: {
    title: 'Analysis',
    subtitle: 'Monitor competitor pricing, website data, and stock levels.',
    newFilter: 'New Filter',
    quickFilters: 'Quick Filters',
    // Analysis page tabs
    tabPrices: 'Price Comparison',
    tabWebsiteInfo: 'Website Info',
    // PriceAnalysis component
    priceAnalysisTitle: 'Analysis',
    priceAnalysisSubtitle: 'Analyze product prices and website information across sources.',
    // Website Info tab
    websiteInfoTitle: 'Website Information',
    websiteInfoDescription: 'Data extracted from secondary modules during crawl (company details, shipping, returns, FAQs, etc.).',
    noModuleResults: 'No Module Data Yet',
    noModuleResultsHint: 'Secondary modules will extract info like company details, shipping policies, and FAQs during the next crawl.',
    modulePagesAnalyzed: '{count} page(s) analyzed',
    moduleSourceUrls: 'Source URLs',
    // Secondary modules states
    secondaryNotEnabled: 'Secondary Modules Not Enabled',
    secondaryNotEnabledHint: 'Enable secondary extraction modules in your task settings to collect company details, shipping info, FAQs, and more from competitor websites.',
    modulesNotTriggered: 'Modules not triggered',
    modulesNotTriggeredHint: 'These modules were enabled but no matching pages were found during the crawl. They may run in future crawls.',
    // Status labels
    statusCompleted: 'Completed',
    statusFailed: 'Failed',
    statusPending: 'Pending',
    extractionFailed: 'Extraction failed',
    noDataExtracted: 'No data extracted yet',
    unknownDomain: 'Unknown domain',
    selectJob: 'Select a job to analyze',
    noJobSelected: 'No job selected',
    selectJobHint: 'Select a project, task, and job above to view price analysis.',
    matchConfig: 'Match Config',
    activeConfig: 'Active Config',
    createConfig: 'Create Config',
    editConfig: 'Edit',
    setActive: 'Set Active',
    deleteConfig: 'Delete',
    runMatching: 'Run Matching',
    catalogModeActive: 'Catalog Mode',
    catalogModeDescription: 'Matching is handled automatically for catalog jobs. To update master prices, upload a CSV with "ean" and "price" columns (optional: "currency").',
    updatePrices: 'Update Prices',
    pricesUpdated: '{{count}} prices updated successfully.',
    pricesNotFound: '{{count}} EANs not found',
    uploadPricesCsvHint: 'Upload a CSV with "ean" and "price" columns to update master prices.',
    matchingInProgress: 'Matching in progress...',
    embeddingImages: 'Analyzing product images...',
    enableImageMatching: 'Enable Image Matching',
    imageWeight: 'Image Weight',
    imageMatchingDescription: 'Compare product images to confirm matches. Uses perceptual hashing for identical images and visual AI for similar images.',
    // Knockout attributes
    knockout: 'Knockout',
    knockoutTooltip: 'If this attribute doesn\'t match, reject the product entirely',
    knockoutEnabled: 'Knockout active',
    knockoutDisabled: 'Knockout inactive',
    // Attribute matching dialog
    primaryAttributesPreEnabled: 'Primary attributes (EAN, MPN, FEFCO etc.) are pre-enabled — toggle them off if not needed.',
    attributeSampleNote: 'Attributes are sampled from the first {count} products. For large catalogs this provides an accurate overview.',
    matchingOverview: 'Matching Overview',
    matchDistribution: 'Match Distribution',
    matchRate: 'Match Rate',
    // Stats
    statProducts: 'Total Products',
    statMatched: 'Matched',
    statUnmatched: 'Unmatched',
    statDomains: 'Sources',
    statLanguages: 'Languages',
    statLanguageGroups: 'Language Groups',
    statVariants: 'With Variants',
    statTotalVariants: 'Total Variants',
    tooltipVariants: 'Products that have multiple variants (e.g. sizes, colors, options).',
    tooltipTotalVariants: 'Total number of product variants across all products with variants.',
    tooltipLanguages: 'Number of different languages detected across all products in this analysis.',
    tooltipLanguageGroups: 'Number of cross-language product groups: the same item linked across languages via EAN or hreflang.',
    // Tooltips
    tooltipTotalProducts: 'Total number of master products from your primary domain that can be compared.',
    tooltipMatched: 'Products that have been successfully matched with comparison products from other sources.',
    tooltipUnmatched: 'Products without a match. Consider adjusting match settings or adding more comparison data.',
    tooltipSources: 'Number of different domains/shops being compared in this analysis.',
    tooltipTier1: 'Tier 1: Exact matches based on unique identifiers like EAN (barcode) or MPN (manufacturer part number). Highest confidence.',
    tooltipTier2: 'Tier 2: Matches based on product attributes like name, dimensions, and specifications. Good confidence but may need verification.',
    tooltipMatchRate: 'Percentage of products that have been matched. Higher is better for comprehensive price comparison.',
    // Pre-filter stats
    statPreFiltered: 'Pre-filtered',
    tooltipPreFiltered: 'Competitor products selected by the pre-filter for normalization. Reduces AI processing costs while maximizing match quality.',
    preFilterLabel: 'Pre-filter',
    preFilterCrawled: 'crawled',
    preFilterSelected: 'selected',
    preFilterNormalized: 'normalized',
    // Filters
    searchPlaceholder: 'Search products...',
    filterAll: 'All Products',
    filterMatched: 'Matched',
    filterUnmatched: 'Unmatched',
    allDomains: 'All Sources',
    allLanguages: 'All Languages',
    colLanguage: 'Language',
    // Table columns
    colProduct: 'Product',
    colSku: 'Art. No.',
    colEan: 'EAN/MPN',
    colSource: 'Source',
    colPrice: 'Price',
    colMatch: 'Match',
    colMatchScore: 'Score',
    colMasterPrice: 'Comp. Price',
    colDifference: 'Difference',
    // Match tiers
    tier1: 'EAN/MPN',
    tier2: 'Attributes',
    noMatch: 'No match',
    noProducts: 'No products found',
    products: 'products',
    // Table Headers (legacy)
    ean: 'EAN',
    sku: 'SKU',
    productName: 'Product Name',
    rrp: 'RRP',
    minPrice: 'Min Price',
    maxPrice: 'Max Price',
    calculatedPrice: 'Calc. Price',
    shipping: 'Shipping',
    appliedRule: 'Applied Rule',
    devLast: 'Dev. Last',
    devAppr: 'Dev. Appr.',
    // Tooltips (legacy)
    offersTooltip: 'Number of active offers across all platforms',
    localMin: 'Local Min',
    localMinTooltip: 'Lowest price found in local region',
    minSeller: 'Min Seller',
    maxDev: 'Max Dev.',
    maxDevTooltip: 'Maximum deviation from RRP',
    range: 'Range',
    rangeTooltip: 'Difference between lowest and highest offer',
    avgPrice: 'Avg Price',
    rating: 'Rating',
    change: 'Change',
    // Filters (legacy)
    brand: 'Brand',
    type: 'Type',
    productGroup: 'Product Group',
    allStrategies: 'All Strategies',
    allResults: 'All Results',
    priceChanged: 'Price Changed',
    calcFailed: 'Calc. Failed',
  },

  // ============================================================================
  // Module Names
  // ============================================================================
  modules: {
    companyInfo: 'Company Information',
    bestSellers: 'Best Sellers',
    contactInfo: 'Contact Information',
    faq: 'FAQ',
    shippingInfo: 'Shipping Info',
    returnPolicy: 'Return Policy',
  },

  // ============================================================================
  // My Pricing
  // ============================================================================
  pricing: {
    title: 'My Pricing',
    subtitle: 'Market overview and pricing management.',
    addProduct: 'Add Product',
    searchPlaceholder: 'EAN, SKU, or Name...',
    // Status
    products: 'Products',
    pricingActive: 'Pricing Active',
    pricingInactive: 'Pricing Inactive',
    pendingApproval: 'Pending Approval',
    noOffers: 'No Offers',
    // Stats
    priceIncreases: 'Price Increases',
    priceDecreases: 'Price Decreases',
    unchanged: 'Unchanged',
    minPrice: 'Min Price',
    maxPrice: 'Max Price',
    rankDistribution: 'Rank Distribution',
    pricingActivity24h: 'Pricing Activity (24h)',
    statusOverview: 'Status Overview',
    // Table columns
    ean: 'EAN',
    sku: 'SKU',
    productName: 'Product Name',
    rrp: 'RRP',
    appliedRule: 'Applied Rule',
    calculatedPrice: 'Calc. Price',
    shipping: 'Shipping',
    devLast: 'Dev. Last',
    devAppr: 'Dev. Appr.',
    // Filters
    ruleStrategy: 'Rule Strategy',
    allStrategies: 'All Strategies',
    allResults: 'All Results',
    priceChanged: 'Price Changed',
    calcFailed: 'Calc. Failed',
    productGroup: 'Product Group',
    allGroups: 'All Groups',
    electronics: 'Electronics',
    furniture: 'Furniture',
    home: 'Home',
    fashion: 'Fashion',
    custom: 'Custom',
  },

  // ============================================================================
  // Product Discovery
  // ============================================================================
  discovery: {
    title: 'Product Discovery',
    subtitle: 'Find products your competitors have that you don\'t.',
    searchProducts: 'Search Products',
    discoverProducts: 'Discover Products',
    searchPlaceholder: 'Search products, brands, EAN...',
    noResults: 'No products found',
    addToMonitoring: 'Add to Monitoring',
    viewProduct: 'View Product',
    // ProductDiscovery component
    selectJob: 'Select a job to analyze',
    findProducts: 'Find products your competitors sell',
    sortBy: 'Sort by',
    sortOpportunity: 'Opportunity Score',
    sortCoverage: 'Competitor Coverage',
    sortPriceLow: 'Price: Low to High',
    sortPriceHigh: 'Price: High to Low',
    sortName: 'Name A-Z',
    minCoverage: 'Min. Competitors',
    any: 'Any',
    atLeast: 'At least',
    competitors: 'competitors',
    totalDiscoveries: 'Discoveries',
    competitorsAnalyzed: 'Competitors',
    jobsAnalyzed: 'Jobs',
    masterDomain: 'Master Domain',
    noProducts: 'No products discovered',
    noProductsDesc: 'Try adjusting filters or select a different job.',
    priceRange: 'Price',
    coverage: 'Coverage',
    coverageTooltip: 'Number of competitors that have this product',
    aiSourceTooltip: 'This product contains AI-generated data. Links and images may be inaccurate.',
    source: 'Source',
    opportunityScoreTooltip: 'Higher score = higher opportunity. Based on competitor coverage and price potential.',
    opportunityScoreLabel: 'Opportunity Score',
    opportunityCoverage: 'Coverage (60%)',
    opportunityPrice: 'Price factor (40%)',
    opportunityPoints: 'pts',
    opportunityFormula: 'Score = (Coverage × 60) + (Price factor × 40)',
    pricePotentialTooltip: 'Estimated profit margin potential based on price variance.',
    products: 'products',
    errorLoading: 'Error loading products',
    viewDetails: 'View Details',
    visit: 'Visit',
    details: 'Details',
    potential: 'profit',
    uncategorized: 'Uncategorized',
    popularSearches: 'Popular:',
  },

  // ============================================================================
  // Content Discovery (Search-based)
  // ============================================================================
  contentDiscovery: {
    title: 'Content Discovery',
    description: 'Search and explore crawled content with full-text and semantic search.',
    selectJob: 'Select Job',
    selectCrawl: 'Select Job & Crawl',
    crawlExecution: 'Crawl Execution',
    noCrawlSelected: 'No Crawl Selected',
    noCrawlSelectedDescription: 'Select a completed crawl to search and explore its content.',
    selectJobFirst: 'Select a Job',
    selectJobFirstDescription: 'Select a project, task, and job to view available crawl data.',
    noCrawlsFound: 'No completed crawls found for this task. Run a crawl job first.',
    searchPlaceholder: 'Search page content, titles, descriptions...',
    semanticSearch: 'Semantic Search',
    embeddingsReady: 'Embeddings ready',
    generatingEmbeddings: 'Generating...',
    noEmbeddings: 'No embeddings',
    generate: 'Generate',
    semanticSearchHelp: 'Semantic search uses AI embeddings to find similar content even when exact words don\'t match. First generate embeddings, then enable semantic search.',
    filters: 'Filters',
    contentType: 'Content Type',
    pageType: 'Page Type',
    signals: 'Signals',
    applyFilters: 'Apply Filters',
    resetFilters: 'Reset',
    results: 'Results',
    noResults: 'No results found',
    enterSearch: 'Enter a search query to find content',
    type: 'Type',
    page: 'Page',
    relevance: 'Score',
  },

  // ============================================================================
  // Product Discovery Detail
  // ============================================================================
  discoveryDetail: {
    productDetails: 'Product Details',
    priceComparison: 'Price Comparison',
    graduatedPrices: 'Graduated Prices',
    competitor: 'Competitor',
    price: 'Price',
    quantity: 'Quantity',
    brand: 'Brand',
    category: 'Category',
    ean: 'EAN',
    mpn: 'MPN',
    noPrice: 'No price',
    cheapest: 'Cheapest',
    mostExpensive: 'Most expensive',
    avgPrice: 'Avg. Price',
    priceSpread: 'Price Spread',
    notMatched: 'Not matched to your catalog',
    error: 'Error loading product',
  },

  // ============================================================================
  // AI Advisor
  // ============================================================================
  aiAdvisor: {
    title: 'AI Strategy Advisor',
    subtitle: 'Real-time actionable insights powered by artificial intelligence.',
    opportunityDetected: 'Opportunity Detected',
    confidence: 'Confidence',
    impact: 'Est. Impact',
    applyAction: 'Apply Recommendation',
    dismiss: 'Dismiss',
    analyzingMarket: 'Analyzing market data...',
    opportunitiesFound: 'Opportunities found',
    reasoning: 'AI Reasoning',
    recommendedAction: 'Recommended Action',
    profitBoost: 'Profit Boost',
    revenueBoost: 'Revenue Boost',
    stockRisk: 'Stock Risk',
    noOpportunities: 'No new optimization opportunities detected at the moment.',
    // Status
    systemOnline: 'System Online',
    systemPaused: 'System Paused',
    allCaughtUp: 'All Caught Up!',
    // Pricing
    currentPrice: 'Current Price',
    recommended: 'Recommended',
    // Live Analysis
    liveAnalysis: 'Live Analysis',
    status: 'STATUS',
    online: 'ONLINE',
    offline: 'OFFLINE',
    latency: 'LATENCY',
    processed24h: 'Processed (24h)',
    accuracy: 'Accuracy',
    // Resource Control
    resourceControl: 'Resource Control',
    activeAnalysis: 'Active Analysis',
    runIndefinitely: 'Run indefinitely',
    startTime: 'Start Time',
    endTime: 'End Time',
    apiCreditsWarning: 'Running analysis consumes API credits.',
    // Coming Soon
    comingSoon: 'Coming Soon',
    comingSoonDescription: 'AI-powered pricing recommendations and market analysis will be available in a future update.',
  },

  // ============================================================================
  // AI Provider Status
  // ============================================================================
  aiProvider: {
    issue: 'AI Provider Issue',
    degradedPerformance: 'degraded performance',
    outage: 'outage',
    pipelineMayBeAffected: 'Pipeline runs may be affected.',
    checkApiKeys: 'Check your AI provider API keys in Settings → AI Models.',
  },

  // ============================================================================
  // Sellers / Competitors
  // ============================================================================
  sellers: {
    title: 'Seller Overview',
    subtitle: 'Comprehensive analysis of all crawled competitors across your price analysis jobs.',
    totalSellers: 'Total Sellers',
    activeSellers: 'Active Sellers',
    newSellers: 'New Sellers',
    sellerName: 'Seller Name',
    platform: 'Platform',
    products: 'Products',
    offers: 'Offers',
    avgRating: 'Avg. Rating',
    belowRrp: 'Below RRP',
    lastSeen: 'Last Seen',
    // SellerOverview specific
    mostOffers: 'Most Offers',
    mostPriceLeaders: 'Most Price Leaders',
    highestDeviation: 'Highest Ø-Deviation',
    searchPlaceholder: 'Search seller...',
    seller: 'Seller',
    sellers: 'sellers',
    leader: 'Leader',
    leaderPct: 'Leader %',
    assortmentPct: 'Assortment %',
    deviation: 'Ø Deviation',
    status: 'Status',
    tooltipOffers: 'Total number of product offers from this seller',
    tooltipProducts: 'Number of unique products',
    tooltipLeader: 'Products where this seller has the lowest price',
    tooltipAssortment: 'Share of price-leading products in total master products',
    tooltipStatus: 'Green: Cheaper, Yellow: Similar, Red: More expensive than master',
    tooltipDeviation: 'Average price deviation from master products',
    lastCrawled: 'Last Crawled',
    jobsCount: 'Analyzed in jobs',
    noSellersFound: 'No sellers found',
    noData: 'No seller data available. Run price analysis jobs with matching enabled to see competitor data.',
    errorLoading: 'Error loading seller data',
    jobsAnalyzed: 'Jobs Analyzed',
    totalOffersStat: 'Total Offers',
    totalProductsStat: 'Total Products',
  },

  // ============================================================================
  // Settings
  // ============================================================================
  settings: {
    title: 'Settings',
    description: 'Configure your crawler, proxies, and integrations.',
    adminRequired: 'Admin Access Required',
    adminRequiredDescription: 'You need administrator privileges to access this section. Contact your system administrator for access.',
    // Tabs
    tabs: {
      team: 'Team & Users',
      proxies: 'Proxies & Network',
      api: 'API & Integrations',
      ai: 'AI Models',
      billing: 'Billing & Credits',
    },
    general: 'General',
    proxies: 'Proxies & Network',
    api: 'API & Integrations',
    email: 'Email / SMTP',
    users: 'Team & Users',
    systemLog: 'System Log',
    // General
    maxConcurrency: 'Max Concurrency',
    requestTimeout: 'Request Timeout (ms)',
    userAgentStrategy: 'User Agent Strategy',
    logRetentionDays: 'Log Retention Days',
    // Proxy
    proxySettings: 'Proxy Settings',
    proxyEnabled: 'Enable Proxy',
    proxyType: 'Proxy Type',
    proxyHost: 'Proxy Host',
    proxyPort: 'Proxy Port',
    proxyUsername: 'Proxy Username',
    proxyPassword: 'Proxy Password',
    proxyCountry: 'Proxy Country',
    testProxy: 'Test Connection',
    proxyConnected: 'Proxy connected successfully',
    proxyFailed: 'Proxy connection failed',
    // API
    apiKeys: 'API Keys',
    anthropicApiKey: 'Anthropic API Key',
    openaiApiKey: 'OpenAI API Key',
    geminiApiKey: 'Gemini API Key',
    testApi: 'Test API',
    apiConnected: 'API connected successfully',
    apiFailed: 'API connection failed',
    // Email
    emailSettings: 'Email Settings',
    smtpServer: 'SMTP Server',
    smtpPort: 'SMTP Port',
    smtpUsername: 'SMTP Username',
    smtpPassword: 'SMTP Password',
    smtpEncryption: 'Encryption',
    smtpSenderName: 'Sender Name',
    smtpSenderEmail: 'Sender Email',
    testEmail: 'Send Test Email',
    emailSent: 'Test email sent successfully',
    emailFailed: 'Failed to send test email',
    // Users
    userManagement: 'User Management',
    addUser: 'Add User',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    username: 'Username',
    role: 'Role',
    lastLogin: 'Last Login',
    resetPassword: 'Reset Password',
    changeRole: 'Change Role',
    adminAccessRequired: 'Admin Access Required',
    adminAccessDescription: 'You need administrator privileges to access this section. Contact your system administrator for access.',
    // Roles
    roleAdmin: 'Admin',
    roleUser: 'User',
    roleViewer: 'Viewer',
    // System Log
    systemLogs: 'System Logs',
    containerLogs: 'Container Logs',
    selectContainer: 'Select Container',
    clearLogs: 'Clear Logs',
    downloadLogs: 'Download Logs',
    streaming: 'Streaming',
    noLogs: 'No logs available',
    // Saved
    settingsSaved: 'Settings saved successfully',
    settingsFailed: 'Failed to save settings',
  },

  // ============================================================================
  // Feedback
  // ============================================================================
  feedback: {
    title: 'Submit Feedback',
    subtitle: 'Share your ideas, report issues, or ask questions.',
    sendFeedback: 'Send Feedback',
    closeFeedback: 'Close feedback',
    shareIdeas: 'Share ideas, report bugs, or request features.',
    failedToSubmit: 'Failed to submit feedback. Please try again.',
    feedbackType: 'Type',
    featureRequest: 'Feature Request',
    improvement: 'Improvement',
    bugReport: 'Bug Report',
    question: 'Question',
    other: 'Other',
    priority: 'Priority',
    priorityLow: 'Low',
    priorityMedium: 'Medium',
    priorityHigh: 'High',
    priorityCritical: 'Critical',
    feedbackTitle: 'Title',
    feedbackTitlePlaceholder: 'Brief summary of your feedback',
    feedbackDescription: 'Description',
    feedbackDescriptionPlaceholder: 'Please describe in detail...',
    titlePlaceholder: 'Brief summary of your feedback...',
    descriptionPlaceholder: 'Please provide details about your feedback...',
    typeLabel: 'Feedback Type',
    priorityLabel: 'Priority',
    component: 'Component (Optional)',
    componentPlaceholder: 'Which part of the app?',
    submitFeedback: 'Submit Feedback',
    submitting: 'Submitting...',
    thankYou: 'Thank you for your feedback!',
    feedbackReceived: "We've received your feedback and will review it shortly.",
    submitError: 'Failed to submit feedback. Please try again.',
    titleMinLength: 'Title must be at least 5 characters',
    descriptionMinLength: 'Description must be at least 10 characters',
  },

  // ============================================================================
  // Billing
  // ============================================================================
  billing: {
    title: 'Billing & Credits',
    subtitle: 'Manage your subscription, credits, and payment methods',
    creditBalance: 'Credit Balance',
    monthlyUsage: 'Monthly Usage',
    currentPlan: 'Current Plan',
    reserved: 'reserved',
    total: 'total',
    ofLimit: 'of limit',
    tier: 'Tier',
    renewsOn: 'Renews on',
    noSubscription: 'No active subscription',
    buyCredits: 'Buy Credits',
    credits: 'credits',
    buy: 'Buy',
    usageBreakdown: 'Usage This Month',
    creditPricing: 'Credit Pricing',
    recentInvoices: 'Recent Invoices',
    invoice: 'Invoice',
    managePayment: 'Manage Payment',
    adminOnly: 'Billing management is restricted to administrators.',
    loadError: 'Failed to load billing information',
    checkoutError: 'Failed to create checkout session',
    portalError: 'Failed to open payment portal',
    comingSoon: 'Billing & Credits',
    comingSoonDescription: 'Credit management, usage tracking, and payment history will be available in a future update.',
    platform_upsell_title: 'Platform Add-On not active',
    platform_upsell_description: 'Monitor Amazon, eBay, Idealo, Otto and more with the Platform Add-On.',
    platform_upsell_cta: 'Book Platform Add-On',
    platform_upsell_price: 'Starting at €79/month',
    credits_available: '{{count}} Credits',
    credits_low: 'Credits low',
    credits_depleted: 'No credits',
    credits_required_short: 'required',
    // Settings tab billing keys
    settings_tab_title: 'Billing & Credits',
    credit_balance: 'Credit Balance',
    subscription_credits: 'Subscription Credits',
    purchased_credits: 'Purchased Credits',
    reserved_credits: 'Reserved',
    available_credits: 'Available',
    expires: 'Expires {{date}}',
    never_expires: 'Never expires',
    current_plan: 'Current Plan',
    renews_on: 'Renews {{date}}',
    upgrade_plan: 'Upgrade Plan',
    manage_subscription: 'Manage Subscription',
    payment_history: 'Payment History',
    no_payments: 'No payments yet.',
    buy_credits: 'Buy Credits',
    manage_payment: 'Manage Payment Method',
    invoice_date: 'Date',
    invoice_amount: 'Amount',
    invoice_status: 'Status',
    invoice_pdf: 'PDF',
    // Trial & setup bonus
    trial_active: 'Free Trial Active',
    trial_days_remaining: '{{count}} days remaining',
    trial_subscribe_cta: 'Subscribe to continue',
    trial_ends_soon: 'Trial ends in {{count}} days',
    setup_bonus_title: 'Welcome bonus active!',
    setup_bonus_description: '+25% first-month credits bonus applied',
    setup_bonus_value: "That's €{{amount}} worth of extra credits",
    // Billing page
    credit_packs_title: 'Purchase Credits',
    credit_packs_subtitle: 'Top up your balance with one-time credit packs',
    usage_chart_title: 'Credit Usage by Action',
    usage_chart_empty: 'No usage data for this period.',
    // Enterprise deployment
    enterprise_subtitle: 'Enterprise deployment — billing managed externally',
    enterprise_banner_title: 'Enterprise Deployment',
    enterprise_badge: 'Enterprise',
    enterprise_banner_subtitle: 'This is a custom enterprise deployment with no credit limits.',
    enterprise_no_credits_title: 'No credit enforcement',
    enterprise_no_credits_desc: 'All operations run against your own API keys. Usage is not metered inside the application.',
    enterprise_billing_title: 'Invoice billing',
    enterprise_billing_desc: 'Your subscription is billed via invoice. Invoices and usage reports are provided by your account manager.',
    enterprise_contact_prefix: 'Questions about your invoice or subscription?',
    // Plan catalog
    plans_title: 'Available Plans',
    plans_subtitle: 'Overview of all subscription plans',
    plans_per_month: '/mo',
    plans_included_credits: '{{count}} credits / month',
    plans_platform_credits: '+ {{count}} platform credits',
    plans_platform_credits_first_purchase: 'first purchase only',
    plans_platform_addon: 'Platform Add-On +{{price}}/mo',
    plans_unlimited: 'Unlimited credits',
    plans_free: 'Custom pricing',
    plans_max_competitors: 'Up to {{count}} competitors',
    // Billing account
    account_title: 'Billing Account',
    account_email_label: 'Billing contact',
    account_domain_label: 'Deployment domain',
    account_status_label: 'Status',
    account_email_note: 'Ensure this email matches the account used during purchase. Contact support if they differ.',
    account_manage: 'Manage Subscription',
    account_cancel: 'Cancel Subscription',
  },

  // ============================================================================
  // Roadmap
  // ============================================================================
  roadmap: {
    title: 'Product Roadmap',
    subtitle: 'Explore upcoming features and improvements planned for the platform',
    // Status labels
    status: {
      completed: 'Completed',
      inProgress: 'In Progress',
      planned: 'Planned',
      future: 'Future',
    },
    // Feature request section
    featureRequest: {
      title: 'Have a feature request?',
      description: 'Share your ideas and help shape the future of the platform',
      hideForm: 'Hide Form',
      submitFeedback: 'Submit Feedback',
    },
    // Feedback form
    feedbackForm: {
      title: 'Submit Your Feedback',
      description: 'Tell us about your idea, report a bug, or ask a question. We read every submission!',
    },
    // API Documentation section
    apiDocs: {
      title: 'API Documentation',
      description: 'Interactive OpenAPI documentation for all services',
      scrapingService: 'Scraping Service',
      scrapingServiceDesc: 'Projects, Tasks, Jobs',
      userService: 'User Service',
      userServiceDesc: 'Auth, Users, Settings',
      crawlerService: 'Crawler Service',
      crawlerServiceDesc: 'Crawling, Extraction, Matching',
    },
    // Roadmap items
    items: {
      priceHistory: {
        title: 'Price History & Tracking',
        description: 'Track competitor price changes over time with detailed charts and historical analysis.',
        features: {
          charts: 'Historical price charts for all products',
          notifications: 'Price change notifications',
          trends: 'Trend analysis and forecasting',
          export: 'Export historical data',
        },
      },
      mlPredictions: {
        title: 'ML-Powered Price Predictions',
        description: 'Use machine learning to predict optimal pricing strategies based on market data.',
        features: {
          recommendations: 'AI-driven price recommendations',
          forecasting: 'Demand forecasting',
          prediction: 'Competitor behavior prediction',
          automated: 'Automated pricing suggestions',
        },
      },
      shopConnectors: {
        title: 'Shop System Connectors',
        description: 'Seamlessly integrate with popular e-commerce platforms for automated price synchronization.',
        features: {
          shopware: 'Shopware 6 integration',
          woocommerce: 'WooCommerce connector',
          shopify: 'Shopify integration',
          autoUpdate: 'Automatic price updates',
        },
      },
      scheduledCrawling: {
        title: 'Scheduled Crawling',
        description: 'Set up automated crawl schedules to keep your data fresh without manual intervention.',
        features: {
          cron: 'Cron-based scheduling',
          priority: 'Priority-based queue',
          rateLimit: 'Rate limit management',
          monitoring: 'Crawl health monitoring',
        },
      },
      marketplaceCrawling: {
        title: 'Marketplace Crawling',
        description: 'Extend crawling capabilities to major marketplaces like Amazon and Idealo.',
        features: {
          amazon: 'Amazon price tracking',
          idealo: 'Idealo integration',
          ebay: 'eBay monitoring',
          multiMarket: 'Multi-marketplace comparison',
        },
      },
      aiAdvisorFull: {
        title: 'AI Advisor',
        description: 'Complete AI-powered pricing advisor with natural language insights and recommendations.',
        features: {
          nlQueries: 'Natural language queries',
          reports: 'Automated reports',
          strategic: 'Strategic recommendations',
          intelligence: 'Market intelligence summaries',
        },
      },
      tokenization: {
        title: 'Tokenization & Credit System',
        description: 'Usage-based credit system with Stripe payment integration for SaaS billing.',
        features: {
          credits: 'Token-based credit system',
          stripe: 'Stripe payment integration',
          usage: 'Usage tracking & metering',
          tiers: 'Subscription tier management',
        },
      },
      dynamicPricing: {
        title: 'Dynamic Pricing',
        description: 'Automated rule-based pricing engine that adjusts prices based on market conditions and competitor data.',
        features: {
          rules: 'Rule-based pricing strategies',
          automation: 'Automated price adjustments',
          margins: 'Margin protection & guardrails',
          sync: 'Shop system price synchronization',
        },
      },
    },
    // Legacy keys (keep for compatibility)
    planned: 'Planned',
    inProgress: 'In Progress',
    completed: 'Completed',
    requestFeature: 'Request Feature',
    upvote: 'Upvote',
    noItems: 'No roadmap items available',
  },

  // ============================================================================
  // Theme / Appearance
  // ============================================================================
  theme: {
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    systemDefault: 'System Default',
    language: 'Language',
  },

  // ============================================================================
  // Errors
  // ============================================================================
  errors: {
    generic: 'Something went wrong. Please try again.',
    notFound: 'Page Not Found',
    notFoundDescription: "The page you're looking for doesn't exist or has been moved.",
    unauthorized: 'Unauthorized',
    unauthorizedDescription: 'You do not have permission to access this page.',
    serverError: 'Server Error',
    serverErrorDescription: 'An error occurred on the server. Please try again later.',
    networkError: 'Network Error',
    networkErrorDescription: 'Unable to connect to the server. Please check your internet connection.',
    timeout: 'Request Timeout',
    timeoutDescription: 'The request took too long. Please try again.',
    goHome: 'Go Home',
    goBack: 'Go Back',
  },

  // ============================================================================
  // Validation
  // ============================================================================
  validation: {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    minLength: 'Must be at least {{min}} characters',
    maxLength: 'Must be at most {{max}} characters',
    passwordMatch: 'Passwords do not match',
    invalidUrl: 'Please enter a valid URL',
    invalidNumber: 'Please enter a valid number',
  },

  // ============================================================================
  // Time
  // ============================================================================
  time: {
    seconds: 'seconds',
    minutes: 'minutes',
    hours: 'hours',
    days: 'days',
    weeks: 'weeks',
    months: 'months',
    years: 'years',
    justNow: 'Just now',
    last30Days: 'Last 30 Days',
    last7Days: 'Last 7 Days',
    last24Hours: 'Last 24 Hours',
  },

  // ============================================================================
  // Product Detail
  // ============================================================================
  productDetail: {
    title: 'Product Details',
    subtitle: 'Detailed analysis and price comparison',
    masterProduct: 'Master Product',
    match: 'Match',
    matches: 'Matches',
    share: 'Share',
    report: 'Report',
    graduatedPrices: 'Graduated Prices',
    graduatedPricesTooltip: 'This product has volume-based pricing (Staffelpreise). Compare prices across different quantity tiers below.',
    graduatedPricesDescription: 'Multiple price tiers detected. Use the comparison table below to compare prices across different order quantities.',
    yourPrices: 'Your prices',
    bestPricePerTier: 'Best price per tier',
    priceTierComparison: 'Price Tier Comparison',
    priceTiers: 'Price Tiers',
    inclTax: 'Incl. Tax',
    exclTax: 'Excl. Tax',
    allMatches: 'All Matches',
    searchMatches: 'Search matches...',
    noMatches: 'No matches found',
    domain: 'Seller',
    productName: 'Product Name',
    matchColumn: 'Match',
    matchTooltip: 'Match tier: Tier 1 = EAN/MPN exact match, Tier 2 = Attribute similarity match',
    score: 'Score',
    price: 'Price',
    priceTooltip: 'Base price (lowest quantity tier if graduated)',
    difference: 'Difference',
    differenceTooltip: 'Price difference compared to your master price',
    matchDetails: 'Match Details',
    productImages: 'Product Images',
    alsoAvailableIn: 'Also available in',
    languageSiblings: 'Language Versions',
    viewInLanguage: 'View in',
    // Variants
    variants: 'Variants',
    variantsTooltip: 'Product variants (sizes, colors, options) extracted from Schema.org data, platform APIs, or page interaction.',
    variantLabel: 'Variant',
    variantSku: 'SKU',
    variantEan: 'EAN',
    variantPrice: 'Price',
    variantStock: 'In Stock',
    variantSource: 'Source',
    variantAttributes: 'Attributes',
    variantDimensions: 'Dimensions',
    noVariants: 'No variants detected for this product.',
    variantComparison: 'Variant Comparison',
    variantComparisonTooltip: 'Compare variants between this product and the matched product.',
    variantOverlapping: 'Matching Variants',
    variantMasterOnly: 'Only in Master',
    variantCompetitorOnly: 'Only in Competitor',
    variantOverlapRatio: 'Overlap',
    variantPriceDiff: 'Price Diff',
    variantPriceThis: 'This Product',
    variantPriceMatch: 'Match',
    hasVariants: 'Variants',
  },

  // ============================================================================
  // Match Overrides
  // ============================================================================
  matching: {
    acceptMatch: 'Accept match',
    rejectMatch: 'Reject match',
    manuallyAccepted: 'Manually accepted',
    manuallyRejected: 'Manually rejected',
    undoOverride: 'Undo',
    overrideReason: 'Reason (optional)',
    attributeGroups: 'Attribute Synonym Groups',
    addGroup: 'Add Group',
    addSynonym: 'Add synonym…',
    groupName: 'Group name',
    synonymsHelp: 'Attributes in the same group are treated as equivalent during matching.',
  },

  // ============================================================================
  // Price History
  // ============================================================================
  priceHistory: {
    title: 'Price History',
    noData: 'No price history available yet. Data will appear after the first re-run.',
    current: 'Current',
    allTimeLow: 'All-Time Low',
    allTimeHigh: 'All-Time High',
    average: 'Average',
    changes: 'Changes',
    timeRange7d: '7 Days',
    timeRange30d: '30 Days',
    timeRange90d: '90 Days',
    timeRange1y: '1 Year',
    timeRangeAll: 'All',
    tiers: 'Tiers',
    basePrice: 'Base Price',
    quantity: 'Qty',
    loadError: 'Failed to load price history',
    competitors: 'Competitors',
    showCompetitors: 'Show competitor prices',
    masterPrice: 'Your price',
    noCompetitorData: 'No competitor price history available',
    configChanged: 'Config changed',
    matchAdded: 'Competitor added',
    matchRemoved: 'Competitor removed',
    matchEvents: 'Match changes',
  },

  // ============================================================================
  // CSV URLs Tab (Task Dialog)
  // ============================================================================
  csvUrls: {
    tab: 'CSV URLs',
    description: 'Upload a CSV file with URLs to crawl. These URLs will be used by all jobs under this task.',
    settingsTab: 'Settings',
    saveFirst: 'Save the task first to upload files',
  },

  // ============================================================================
  // Catalog Mode
  // ============================================================================
  catalog: {
    modeLabel: 'Upload Mode',
    modeUrl: 'URL Mode',
    modeUrlDescription: 'Crawl product URLs to extract and compare prices.',
    modeCatalog: 'Catalog Mode',
    modeCatalogDescription: 'Upload a product catalog (CSV) with names and EANs. Platforms are searched directly.',
    tab: 'Catalog',
    settingsTab: 'Settings',
    description: 'Upload a CSV with product names and EANs. The system searches platforms directly — no master URL crawling needed.',
    resultsPerPlatform: 'Results per platform',
    resultsPerPlatformDescription: 'Number of search results to fetch per platform per product (1-5).',
    dropOrClick: 'Drop catalog CSV here or click to browse',
    limits: 'Max 5MB, up to 10,000 products',
    uploading: 'Uploading catalog...',
    uploadSuccess: '{{count}} products uploaded successfully',
    uploadFailed: 'Catalog upload failed',
    productsUploaded: 'products uploaded',
    missingEan: '{{count}} products without EAN',
    replaceCsv: 'Replace CSV',
    clearAll: 'Clear All',
    clearSuccess: 'Catalog cleared',
    eanSearch: 'EAN search',
    nameSearch: 'Name search',
    platformNoMasterRequired: 'Select platforms to search. No master URL required in catalog mode.',
    languageTitle: 'Result Language',
    languageLabel: 'Language',
    languageDescription: 'Select the language you want search results in. The platform locale (e.g. Amazon DE vs. Amazon FR) is determined by your platform selection above.',
    languageHint: 'This language is used for product name matching and result filtering. Platform-specific locales are handled automatically based on your selected platforms.',
    modeLocked: 'Locked — mode cannot be changed after jobs have been created',
    totalProducts: '{{count}} catalog products',
    downloadCsv: 'Download CSV',
    colName: 'Name',
    colEan: 'EAN',
    colPrice: 'Price',
    csvColumnsHint: 'Required: "name", "ean". Optional: "price", "currency", "external_id", "variant_id".',
  },

  // ============================================================================
  // CSV URL Upload
  // ============================================================================
  csvUpload: {
    dropOrClick: 'Drop CSV file here or click to browse',
    limits: 'Max 25MB (no URL count limit)',
    uploading: 'Uploading...',
    uploadSuccess: '{{count}} URLs uploaded successfully',
    uploadFailed: 'Upload failed',
    fileTooLarge: 'File is too large. Maximum size is 5MB.',
    invalidFileType: 'Invalid file type. Please upload a .csv or .txt file.',
    urlsUploaded: 'URLs uploaded',
    replaceCsv: 'Replace CSV',
    clearAll: 'Clear All',
    clearSuccess: '{{count}} URLs cleared',
    clearFailed: 'Failed to clear URLs',
    invalidUrls: 'invalid URLs skipped',
    row: 'Row',
    more: 'more',
    languageMismatch: 'TLD/language mismatch detected',
    configuredAs: 'configured as',
    duplicatesSkipped: 'duplicate URLs skipped',
    csvColumnsTitle: 'CSV columns:',
    csvColumnsRequired: 'Required',
    csvColumnsOptional: 'Optional',
    csvColumnsHint: 'Header row is required. Delimiter is auto-detected (comma or semicolon).',
  },

  // ============================================================================
  // Uploaded URLs List
  // ============================================================================
  uploadedUrls: {
    title: 'Uploaded URLs',
    total: '{{count}} URLs total',
    downloadCsv: 'Download CSV',
    pending: 'Pending',
    crawled: 'Crawled',
    failed: 'Failed',
    skipped: 'Skipped',
    filterAll: 'All statuses',
    noLabel: '—',
    noLanguage: '—',
    page: 'Page {{page}} of {{totalPages}}',
    previous: 'Previous',
    next: 'Next',
    errorDetails: 'Error details',
    empty: 'No URLs uploaded',
    loading: 'Loading URLs...',
    showUrls: 'Show ({{count}})',
    hideUrls: 'Hide',
  },

  // ============================================================================
  // Crawl Entry Seeds
  // ============================================================================
  entrySeeds: {
    label: 'Crawl Entry Points',
    description: 'The crawler starts from these pages and follows all linked product pages. Leave empty to start from the domain root.',
    placeholder: '/products/ or https://domain.com/products/',
  },

  // Master Data Import
  masterData: {
    title: 'Import Master Data',
    description: 'Pre-seed master product data for {{domain}}',
    howItWorks: 'Upload your own product data as a CSV. The pipeline will skip crawling your shop and use this data directly for matching against competitors.',
    expectedColumns: 'Expected columns:',
    requiredColumn: '* = required',
    dropCsv: 'Drop your CSV file here or',
    clickBrowse: 'click to browse',
    csvOnly: 'CSV or TXT, max 25MB',
    importAndContinue: 'Import & Continue',
    uploadAnother: 'Upload Another',
    imported: '{{count}} products imported',
    buttonImport: 'Import Master Data',
    noMasterDomain: 'Set a Master URL first to import product data.',
  },

  // Connectors
  connectors: {
    title: 'Shop Connectors',
    description: 'Connect your shop system to sync master product data automatically.',
    addConnector: 'Add Connector',
    testConnection: 'Test Connection',
    syncNow: 'Sync Now',
    lastSync: 'Last sync: {{date}}',
    never: 'Never',
    shopware: 'Shopware 6',
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
    apiUrl: 'API URL',
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    salesChannelId: 'Sales Channel ID',
    storeUrl: 'Store URL',
    accessToken: 'Access Token',
    consumerKey: 'Consumer Key',
    consumerSecret: 'Consumer Secret',
    connectionSuccess: 'Connection successful!',
    connectionFailed: 'Connection failed. Check your credentials.',
    syncSuccess: 'Synced {{count}} products',
    syncFailed: 'Sync failed',
    noConnectors: 'No shop connections yet',
    noConnectorsDesc: 'Connect your Shopware, WooCommerce, or Shopify store to sync master product data.',
    syncToTask: 'Sync to Task',
    selectTask: 'Select a task to sync products into',
    selectTaskDesc: 'Choose which task receives the synced products. Each task applies its own filters.',
    managedOnPricing: 'Shop connections are managed on the My Pricing page.',
    projectLabel: 'Project',
    deleteConfirm: 'Delete this connector? Synced products will remain.',
    syncSuccessWithTask: 'Synced {{count}} products to {{task}}',
  },

  // ============================================================================
  // Scheduled Crawls
  // ============================================================================
  scheduling: {
    // Page
    title: 'Scheduled Crawls',
    subtitle: 'Automate your crawl jobs with scheduled execution.',

    // Table headers
    jobName: 'Job Name',
    project: 'Project',
    schedule: 'Schedule',
    mode: 'Mode',
    lastRun: 'Last Run',
    nextRun: 'Next Run',

    // Status
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusFailed: 'Failed',
    statusRunning: 'Running',

    // Filters
    filterAll: 'All',
    filterActive: 'Active',
    filterPaused: 'Paused',
    filterFailed: 'Failed',

    // Actions
    createSchedule: 'Create Schedule',
    editSchedule: 'Edit Schedule',
    deleteSchedule: 'Delete Schedule',
    pauseSchedule: 'Pause',
    resumeSchedule: 'Resume',
    runNow: 'Run Now',
    viewRuns: 'View Runs',
    retryRun: 'Retry',
    discardRun: 'Discard',

    // Empty state
    noSchedules: 'No scheduled crawls yet.',
    noSchedulesHint: 'Create one to automate your crawl jobs.',

    // Delete confirmation
    confirmDelete: 'Are you sure you want to delete this schedule? This cannot be undone.',
    confirmDiscard: 'Are you sure you want to discard this run? The results will be removed.',

    // Schedule display
    everyNHours: 'Every {value} hours',
    everyNDays: 'Every {value} days',
    everyNWeeks: 'Every {value} weeks',
    everyNMonths: 'Every {value} months',
    cronSchedule: 'Cron: {expression}',

    // Crawl modes
    modeReextract: 'Re-extract Data',
    modeReextractDesc: 'Re-fetch existing URLs with fresh extraction, normalization, and matching',
    modeRecrawl: 'Re-crawl & Find New URLs',
    modeRecrawlDesc: 'Full crawl to find new product pages, then extract, normalize, and match',
    modeRenormalize: 'Re-normalize + Match',
    modeRenormalizeDesc: 'Re-run AI normalization and matching on existing extractions (no crawl)',
    modeRefreshPlatforms: 'Refresh Platforms Only',
    modeRefreshPlatformsDesc: 'Re-scrape platform prices without touching shop/competitor data',
    modeReextractSelectors: 'Refresh Selectors + Re-extract',
    modeReextractSelectorsDesc: 'Re-discover page selectors first, then re-extract with new selectors',
    modeRediscover: 'Rediscover',
    modeRediscoverDesc: 'Find new and changed pages.',
    modeFullFresh: 'Full Fresh Start',
    modeFullFreshDesc: 'Re-discover selectors + full re-crawl from scratch. Resets normalization history.',

    // Create dialog
    wizardTitle: 'Create Schedule',
    wizardEditTitle: 'Edit Schedule',
    stepJob: 'Job',
    stepTiming: 'Timing',
    stepMode: 'Mode',
    stepPipeline: 'Pipeline',
    stepNotifications: 'Notifications',
    stepAdvanced: 'Advanced',

    // Pipeline step
    pipelineFlow: 'Pipeline Flow',
    pipelineCrawl: 'Crawl',
    pipelineExtract: 'Extract',
    pipelineNormalize: 'Normalize',
    pipelineMatch: 'Match',
    pipelineAiDesc: 'AI-powered normalization produces the highest quality results. Uses Claude — costs credits.',
    pipelineSelectorDesc: 'Selector-based normalization reuses extraction selectors. Free — no AI calls, no credit cost.',
    normalizationStrategy: 'Normalization',
    normModeAi: 'AI-Powered',
    normModeAiDesc: 'Uses Claude to normalize product data. Best quality, credits required.',
    normModeSelector: 'Selector-Based',
    normModeSelectorDesc: 'Reuses page selectors for normalization. Free, no AI cost. Good for consistent page structures.',
    normCredits: 'Credits',
    normFree: 'Free',

    // Step 1: Job selection
    selectJob: 'Select a job',
    selectJobHint: 'Choose a completed job to schedule recurring crawls.',
    noJobsAvailable: 'No completed jobs available.',

    // Step 2: Timing
    scheduleType: 'Schedule Type',
    typeInterval: 'Interval',
    typeCron: 'Cron Expression',
    intervalValue: 'Every',
    intervalUnit: 'Unit',
    unitHours: 'Hours',
    unitDays: 'Days',
    unitWeeks: 'Weeks',
    unitMonths: 'Months',
    cronExpression: 'Cron Expression',
    cronHelp: 'Standard 5-field cron format (min hour dom mon dow)',
    timezone: 'Timezone',
    presets: 'Quick Presets',
    presetDaily: 'Daily at 6:00 AM',
    presetWeekly: 'Weekly on Monday',
    presetBiweekly: 'Every 2 Weeks',
    presetMonthly: 'Monthly on the 1st',

    // Step 3: Crawl mode
    crawlMode: 'Crawl Mode',
    autoNormalize: 'Auto-normalize results',
    autoMatch: 'Auto-match products',
    autoMatchDesc: 'After normalization, automatically match products against the master for price comparison.',

    // Step 4: Notifications
    notifyOnFailure: 'Notify on failure',
    notifyOnSuccess: 'Notify on success',
    notifyOnPriceChange: 'Notify on price changes',
    priceChangeThreshold: 'Price change threshold (%)',
    notificationEmails: 'Notification emails',
    addEmail: 'Add email',

    // Step 5: Advanced
    maxFailures: 'Max consecutive failures before auto-pause',
    maxFailuresHint: 'The schedule will be paused automatically after this many consecutive failures.',

    // Run history
    runHistory: 'Run History',
    runNumber: '#',
    runDate: 'Date',
    runDuration: 'Duration',
    runMode: 'Mode',
    runStatus: 'Status',
    runProducts: 'Products',
    runPriceChanges: 'Price Changes',
    totalRuns: 'Total Runs',
    successRate: 'Success Rate',
    avgDuration: 'Avg Duration',
    lastPriceChange: 'Last Price Change',
    noRuns: 'No runs yet.',
    noRunsHint: 'This schedule has not been executed yet.',

    // Run status
    runQueued: 'Queued',
    runRunning: 'Running',
    runSuccess: 'Success',
    runFailed: 'Failed',
    runDiscarded: 'Discarded',

    // Errors
    errorLoading: 'Failed to load schedules.',
    errorCreating: 'Failed to create schedule.',
    errorUpdating: 'Failed to update schedule.',
    errorDeleting: 'Failed to delete schedule.',
    errorPausing: 'Failed to pause schedule.',
    errorResuming: 'Failed to resume schedule.',
    errorRunNow: 'Failed to trigger immediate run.',
    errorRetry: 'Failed to retry run.',
    errorDiscard: 'Failed to discard run.',

    // Success messages
    created: 'Schedule created successfully.',
    updated: 'Schedule updated successfully.',
    deleted: 'Schedule deleted successfully.',
    paused: 'Schedule paused.',
    resumed: 'Schedule resumed.',
    runTriggered: 'Immediate run triggered.',
    runRetried: 'Run retry triggered.',
    runDiscardedSuccess: 'Run discarded.',

    // Misc
    consecutiveFailures: '{count} consecutive failures',
    nextRunIn: 'Next run in {time}',
    lastRunAgo: '{time} ago',
    neverRun: 'Never',
  },

  // ============================================================================
  // Dynamic Refresh
  // ============================================================================
  refresh: {
    mode: {
      forceAll: 'Force All',
      forceAllDesc: 'Refresh all configured entities regardless of schedule',
      dueOnly: 'Due Only',
      dueOnlyDesc: 'Only refresh entities past their interval',
      customSelection: 'Custom Selection',
      customSelectionDesc: 'Choose specific entities to refresh',
      customSelectionHint: 'Select entities below to include in this refresh run.',
    },
    preview: {
      loading: 'Loading preview...',
      title: 'Refresh Preview',
      toRefresh: 'to refresh',
      skipped: 'not due',
      byType: 'By type',
    },
    policy: {
      title: 'Refresh Policy',
      defaultInterval: 'Default interval',
      hours: 'hours',
      master: 'Master Domain',
      competitors: 'Competitors',
      platforms: 'Platforms',
      inherited: 'Using default',
    },
    state: {
      lastSuccess: 'Last success',
      lastAttempt: 'Last attempt',
      consecutiveFailures: 'Consecutive failures',
      never: 'Never',
      hoursDue: 'Due in {{hours}} hours',
      overdue: 'Overdue by {{hours}} hours',
    },
  },
  refreshPicker: {
    title: 'Select Entities to Refresh',
    loading: 'Loading entities…',
    selected: 'selected',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    startRefresh: 'Start Refresh',
    noEntities: 'No entities found for this task.',
  },

  // ============================================================================
  // Run Action Modal
  // ============================================================================
  action: {
    title: 'Run Action',
    runAction: 'Run Action',
    selectAction: 'Action',
    entityScope: 'Entity Scope',
    allEntitiesIncluded: 'All entities included — this action runs on the full dataset.',

    // Normal extraction actions
    refreshData: 'Refresh Data',
    refreshDataDesc: 'Re-crawl existing URLs with existing selectors, then normalize and match.',
    refreshSelectors: 'Refresh Selectors',
    refreshSelectorsDesc: 'Re-run AI selector discovery to update CSS selectors without re-extracting data',
    startFresh: 'Start Fresh',
    startFreshDesc: 'Full pipeline from scratch — re-discover selectors, crawl all pages, normalize, and match.',
    startFreshWarning: 'This will re-discover all selectors and re-crawl everything. Previous data will be replaced.',
    startFreshManualDesc: 'Full pipeline from scratch — crawl all pages with manual selectors, normalize, and match.',
    startFreshManualWarning: 'This will re-crawl everything using manual selectors. Previous data will be replaced.',
    custom: 'Custom Run',
    customDesc: 'Configure which pipeline steps to run and which entities to include.',
    resume: 'Resume Pipeline',
    resumeDesc: 'Resume the failed pipeline from the {phase} step.',

    // Catalog actions
    catalogRefreshPlatforms: 'Refresh Platforms',
    catalogRefreshPlatformsDesc: 'Re-scrape all platforms with current catalog products, re-match results',

    catalogFullFresh: 'Full Fresh Start',
    catalogFullFreshDesc: 'Re-read catalog CSV, re-create virtual masters, re-scrape all platforms',
    catalogFullFreshWarning: 'This will discard all existing data and start over.',

    // Discovery actions
    refreshResults: 'Refresh Results',
    refreshResultsDesc: 'Re-crawl the same URLs from the last run to get updated content',
    recrawlDiscover: 'Re-crawl & Discover',
    recrawlDiscoverDesc: 'Fresh crawl from task URLs to find new pages',

    // Modules-only actions
    modulesRecrawl: 'Re-crawl',
    modulesRecrawlDesc: 'Re-crawl all configured URLs to find new pages',
    modulesFullFresh: 'Full Fresh Start',
    modulesFullFreshDesc: 'Full re-crawl from scratch',
    modulesFullFreshWarning: 'This will discard all existing data and start over.',

    // Mode badges
    modeCatalog: 'Catalog',
    modeDiscovery: 'Discovery',
    modeModules: 'Modules',
    modeManual: 'Manual',

    // UI strings
    loadingEntities: 'Loading entities…',
    selected: 'selected',
    groupMaster: 'Master',
    groupCompetitors: 'Competitors',
    groupPlatforms: 'Platforms',
    running: 'Running…',
    run: 'Run',
  },

  // Pipeline Phase Labels
  // ============================================================================
  pipeline: {
    discovering: 'Discovering selectors',
    crawling: 'Crawling',
    extracting: 'Extracting',
    normalizing: 'Normalizing',
    preFiltering: 'Pre-filtering',
    normalizingCompetitors: 'Normalizing competitors',
    matching: 'Matching',
    platformScraping: 'Platform Scraping',
    fieldMapping: 'Field mapping',
    running: 'Running',
    failed: 'Failed',
    cancelled: 'Cancelled',
    complete: 'Complete',
    ready: 'Ready',
    extracted: 'Extracted',
    readyToRun: 'Ready to run pipeline',
    inProgress: 'Pipeline in progress',
  },

  // Pipeline Step Toggles (Custom Run)
  // ============================================================================
  pipelineSteps: {
    title: 'Pipeline Steps',
    crawl: 'Crawl (discover URLs)',
    extract: 'Extract (apply selectors)',
    normalize: 'Normalize / Field-map',
    matching: 'Match products',
    platforms: 'Platform scraping',
    freshUrls: 'Use fresh URLs',
    freshUrlsDesc: 'Re-discover URLs instead of reusing existing ones',
    skipCrawl: 'No existing crawl data — crawl step required',
    skipExtract: 'No existing extraction data — extract step required',
    skipNormalize: 'Skip normalization',
    skipMatching: 'Skip matching',
    skipPlatforms: 'Skip platform scraping',
  },

  // Execution Mode Step (Job Creation Wizard Step 3)
  // ============================================================================
  executionMode: {
    title: 'Execution Mode',
    description: 'Choose how the pipeline should run after job creation.',
    automatic: 'Automatic Mode',
    recommended: 'Recommended',
    automaticDesc: 'The full pipeline runs automatically after job creation — AI selectors, normalization, matching, and platform scraping.',
    manual: 'Manual Mode',
    advanced: 'Advanced',
    manualDesc: 'You control which pipeline steps to run. Start each step manually from the job dashboard.',
    pipelineSteps: 'Pipeline Steps',
    aiSelectorDiscovery: 'AI Selector Discovery',
    aiSelectorsSkippedHint: 'Skipped — you must configure domain selectors manually before extracting.',
    aiSelectorsEnabledHint: 'AI will analyze pages and discover CSS selectors automatically.',
    normalization: 'Normalization',
    normalizationHint: 'Standardize and clean extracted product data using AI.',
    matching: 'Matching',
    matchingHint: 'Match master products with competitor products for comparison.',
  },


};
